import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { authFetch } from './authFetch';
import { UserContext } from './App';
import { Search, Phone, Calendar, Play, Download, ChevronRight, ChevronDown, PhoneIncoming, PhoneOutgoing, Loader2 } from 'lucide-react';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const RECORDINGS_PER_PAGE = 50;

function CallRecordings() {
  const { currentUser } = useContext(UserContext);
  const [months, setMonths] = useState([]);
  const [days, setDays] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(null);
  const [currentDay, setCurrentDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [playingUrl, setPlayingUrl] = useState(null);
  const [originalUrl, setOriginalUrl] = useState(null);
  const [playingRecording, setPlayingRecording] = useState(null);
  const [expandedYears, setExpandedYears] = useState({});
  const [loadingAudio, setLoadingAudio] = useState(false);
  // Pagination state
  const [totalRecordings, setTotalRecordings] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  // Check if user is admin
  const isAdmin = currentUser?.is_staff ||
    currentUser?.is_superuser ||
    (currentUser?.groups || []).some(g => (g.name || '').toLowerCase() === 'admin');

  // Fetch available months on mount
  useEffect(() => {
    if (isAdmin) {
      fetchMonths();
    }
  }, [isAdmin]);

  const fetchMonths = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(`${BASE_URL}/api/recordings/months/`);
      if (response.ok) {
        const data = await response.json();
        setMonths(data.months || []);
      } else if (response.status === 403) {
        setError('Admin access required');
      } else {
        setError('Failed to load recordings');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Error fetching months:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDays = async (month) => {
    setLoading(true);
    setError(null);
    setCurrentMonth(month);
    setCurrentDay(null);
    setDays([]);
    setRecordings([]);
    setSearchResults(null);

    try {
      const response = await authFetch(`${BASE_URL}/api/recordings/days/${month}/`);
      if (response.ok) {
        const data = await response.json();
        setDays(data.days || []);
      } else {
        setError('Failed to load days');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Error fetching days:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecordings = async (date, page = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setCurrentDay(date);
      setSearchResults(null);
      setCurrentPage(1);
    }
    setError(null);

    try {
      const offset = (page - 1) * RECORDINGS_PER_PAGE;
      const response = await authFetch(
        `${BASE_URL}/api/recordings/date/${date}/?limit=${RECORDINGS_PER_PAGE}&offset=${offset}`
      );
      if (response.ok) {
        const data = await response.json();
        if (append) {
          setRecordings(prev => [...prev, ...(data.recordings || [])]);
        } else {
          setRecordings(data.recordings || []);
        }
        setTotalRecordings(data.total || 0);
        setCurrentPage(page);
      } else {
        setError('Failed to load recordings');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Error fetching recordings:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreRecordings = () => {
    if (currentDay && !loadingMore) {
      fetchRecordings(currentDay, currentPage + 1, true);
    }
  };

  const hasMoreRecordings = recordings.length < totalRecordings;

  const playRecording = async (recording) => {
    try {
      setLoadingAudio(true);
      setPlayingRecording(recording);
      setError(null);

      const response = await authFetch(`${BASE_URL}/api/recordings/play/${encodeURIComponent(recording.key)}/`);
      if (response.ok) {
        const data = await response.json();
        setPlayingUrl(data.url);
        setOriginalUrl(data.url);
      } else {
        setError('Failed to get recording URL');
        setPlayingRecording(null);
      }
    } catch (err) {
      setError('Error loading recording');
      setPlayingRecording(null);
      console.error('Error playing recording:', err);
    } finally {
      setLoadingAudio(false);
    }
  };

  const downloadRecording = async () => {
    if (!originalUrl || !playingRecording) return;

    try {
      const response = await fetch(originalUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const ext = playingRecording.key?.endsWith('.mp3') ? 'mp3' : 'wav';
      const a = document.createElement('a');
      a.href = url;
      a.download = `recording_${playingRecording.from_number}_${playingRecording.date.replace(/\//g, '-')}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      window.open(originalUrl, '_blank');
    }
  };

  const closePlayer = () => {
    setPlayingUrl(null);
    setOriginalUrl(null);
    setPlayingRecording(null);
  };

  const handleSearch = useCallback(async () => {
    if (searchQuery.trim().length < 4) {
      setSearchResults(null);
      return;
    }

    setSearching(true);
    try {
      const response = await authFetch(`${BASE_URL}/api/recordings/search/?q=${encodeURIComponent(searchQuery)}`);
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.results || []);
      }
    } catch (err) {
      console.error('Error searching:', err);
    } finally {
      setSearching(false);
    }
  }, [searchQuery]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim().length >= 4) {
        handleSearch();
      } else {
        setSearchResults(null);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length === 11 && cleaned[0] === '1') {
      return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  const formatMonthOnly = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'long' });
  };

  const formatDayDisplay = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    const date = new Date(year, parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  // Group months by year
  const monthsByYear = useMemo(() => {
    const grouped = {};
    months.forEach(month => {
      const year = month.split('-')[0];
      if (!grouped[year]) {
        grouped[year] = [];
      }
      grouped[year].push(month);
    });
    const sortedYears = Object.keys(grouped).sort((a, b) => b - a);
    return { grouped, sortedYears };
  }, [months]);

  // Auto-expand the most recent year on load
  useEffect(() => {
    if (monthsByYear.sortedYears.length > 0 && Object.keys(expandedYears).length === 0) {
      setExpandedYears({ [monthsByYear.sortedYears[0]]: true });
    }
  }, [monthsByYear.sortedYears]);

  const toggleYear = (year) => {
    setExpandedYears(prev => ({
      ...prev,
      [year]: !prev[year]
    }));
  };

  const goHome = () => {
    setCurrentMonth(null);
    setCurrentDay(null);
    setDays([]);
    setRecordings([]);
    setSearchResults(null);
    setSearchQuery('');
    setTotalRecordings(0);
    setCurrentPage(1);
    closePlayer();
  };

  const goToMonth = () => {
    setCurrentDay(null);
    setRecordings([]);
    setTotalRecordings(0);
    setCurrentPage(1);
    closePlayer();
  };

  // Access denied for non-admins
  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <h2 className="text-xl font-bold text-red-700 mb-2">Access Denied</h2>
          <p className="text-red-600">Call recordings are only accessible to administrators.</p>
        </div>
      </div>
    );
  }

  // Render year/month accordion
  const renderYearMonthList = () => (
    <div className="space-y-3">
      {monthsByYear.sortedYears.map((year) => (
        <div key={year} className="border border-gray-200 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleYear(year)}
            className="w-full bg-gray-50 px-4 py-3 flex items-center justify-between hover:bg-gray-100 transition"
          >
            <div className="flex items-center">
              <Calendar className="h-5 w-5 text-blue-600 mr-2" />
              <span className="text-lg font-semibold text-gray-800">{year}</span>
              <span className="ml-2 text-sm text-gray-500">
                ({monthsByYear.grouped[year].length} months)
              </span>
            </div>
            {expandedYears[year] ? (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronRight className="h-5 w-5 text-gray-500" />
            )}
          </button>

          {expandedYears[year] && (
            <div className="p-4 bg-white grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {monthsByYear.grouped[year].map((month) => (
                <button
                  key={month}
                  onClick={() => fetchDays(month)}
                  className="bg-gray-50 border border-gray-200 rounded-lg p-3 hover:bg-blue-50 hover:border-blue-300 transition text-left"
                >
                  <span className="font-medium text-gray-700">{formatMonthOnly(month)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  // Render days list for selected month
  const renderDaysList = () => (
    <div className="space-y-2">
      {days.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No recordings found for this month.</p>
      ) : (
        days.map((day) => (
          <button
            key={day.date}
            onClick={() => fetchRecordings(day.date)}
            className="w-full bg-white border border-gray-200 rounded-lg p-4 hover:bg-blue-50 hover:border-blue-300 transition text-left flex items-center justify-between"
          >
            <div className="flex items-center">
              <Calendar className="h-5 w-5 text-blue-600 mr-3" />
              <div>
                <span className="font-medium text-gray-800">{day.weekday}, {day.display.split(',')[0]}</span>
              </div>
            </div>
            <div className="flex items-center">
              <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
                {day.count} recording{day.count !== 1 ? 's' : ''}
              </span>
              <ChevronRight className="h-5 w-5 text-gray-400 ml-2" />
            </div>
          </button>
        ))
      )}
    </div>
  );

  // Render recording list
  const renderRecordingList = (items, showMonth = false) => (
    <div className="space-y-2">
      {items.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No recordings found.</p>
      ) : (
        items.map((rec, index) => (
          <div
            key={index}
            className="bg-white border border-gray-200 rounded-lg p-4 hover:bg-gray-50"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  {rec.direction === 'inbound' ? (
                    <PhoneIncoming className="h-4 w-4 text-green-500 mr-2" />
                  ) : rec.direction === 'outbound' ? (
                    <PhoneOutgoing className="h-4 w-4 text-blue-500 mr-2" />
                  ) : (
                    <Phone className="h-4 w-4 text-gray-500 mr-2" />
                  )}
                  <span className="font-medium">
                    {formatPhoneNumber(rec.from_number)} → {formatPhoneNumber(rec.to_number)}
                  </span>
                  {rec.from_name && (
                    <span className="ml-2 text-gray-600">({rec.from_name})</span>
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  {showMonth && <span className="mr-3">{rec.month}</span>}
                  <span>{rec.time}</span>
                  {rec.duration_formatted && (
                    <>
                      <span className="mx-2">•</span>
                      <span>{rec.duration_formatted}</span>
                    </>
                  )}
                  {rec.customer_name && (
                    <>
                      <span className="mx-2">•</span>
                      <span className="text-blue-600 font-medium">{rec.customer_name}</span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => playRecording(rec)}
                  disabled={loadingAudio && playingRecording?.key === rec.key}
                  className={`${playingRecording?.key === rec.key ? 'bg-blue-600' : 'bg-green-600'} text-white px-3 py-1 rounded hover:opacity-90 transition flex items-center disabled:opacity-50`}
                >
                  {loadingAudio && playingRecording?.key === rec.key ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      Loading...
                    </>
                  ) : playingRecording?.key === rec.key && playingUrl ? (
                    <>
                      <Play className="h-4 w-4 mr-1" />
                      Playing
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-1" />
                      Play
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center">
            <Phone className="h-6 w-6 mr-2 text-blue-600" />
            Call Recordings
          </h1>
          {(currentMonth || currentDay) && (
            <button
              onClick={goHome}
              className="text-blue-600 hover:text-blue-800 flex items-center"
            >
              ← Back to Months
            </button>
          )}
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by phone number (min 4 digits)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {searching && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
              </div>
            )}
          </div>
        </div>

        {/* Breadcrumbs */}
        {currentMonth && !searchResults && (
          <div className="flex items-center mb-4 text-sm text-gray-600">
            <button onClick={goHome} className="text-blue-600 hover:text-blue-800">
              All Months
            </button>
            <ChevronRight className="h-4 w-4 mx-2" />
            {currentDay ? (
              <>
                <button onClick={goToMonth} className="text-blue-600 hover:text-blue-800">
                  {formatMonth(currentMonth)}
                </button>
                <ChevronRight className="h-4 w-4 mx-2" />
                <span className="font-medium">{formatDayDisplay(currentDay)}</span>
                <span className="ml-2 text-gray-400">
                  (showing {recordings.length} of {totalRecordings} recordings)
                </span>
              </>
            ) : (
              <>
                <span className="font-medium">{formatMonth(currentMonth)}</span>
                <span className="ml-2 text-gray-400">({days.length} days with recordings)</span>
              </>
            )}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
            <span className="ml-3 text-gray-600">Loading...</span>
          </div>
        )}

        {/* Content */}
        {!loading && (
          <>
            {searchResults ? (
              <div>
                <h3 className="text-lg font-semibold mb-3">
                  Search Results ({searchResults.length})
                </h3>
                {renderRecordingList(searchResults, true)}
              </div>
            ) : !currentMonth ? (
              <>
                <p className="text-gray-600 mb-4">
                  Select a year and month to browse call recordings.
                </p>
                {months.length > 0 ? (
                  renderYearMonthList()
                ) : (
                  <p className="text-gray-500 text-center py-8">
                    No recordings available yet.
                  </p>
                )}
              </>
            ) : !currentDay ? (
              <>
                <p className="text-gray-600 mb-4">
                  Select a day to view recordings.
                </p>
                {renderDaysList()}
              </>
            ) : (
              <>
                {renderRecordingList(recordings)}
                {/* Load More Button */}
                {hasMoreRecordings && (
                  <div className="mt-6 text-center">
                    <button
                      onClick={loadMoreRecordings}
                      disabled={loadingMore}
                      className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center mx-auto"
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          Load More ({totalRecordings - recordings.length} remaining)
                        </>
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Footer */}
        {!currentMonth && !loading && months.length > 0 && (
          <div className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
            Call recordings are retained indefinitely and are only accessible to administrators.
          </div>
        )}
      </div>

      {/* Sticky Audio Player at Bottom */}
      {(playingUrl || loadingAudio) && playingRecording && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-900 text-white p-4 shadow-lg z-50">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-4">
                <div>
                  <div className="font-medium">
                    {formatPhoneNumber(playingRecording.from_number)} → {formatPhoneNumber(playingRecording.to_number)}
                  </div>
                  <div className="text-sm text-gray-400">
                    {playingRecording.date} {playingRecording.time}
                    {playingRecording.customer_name && ` • ${playingRecording.customer_name}`}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={downloadRecording}
                  disabled={!playingUrl}
                  className="text-gray-300 hover:text-white flex items-center text-sm disabled:opacity-50"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download
                </button>
                <button
                  onClick={closePlayer}
                  className="text-gray-400 hover:text-white text-xl"
                >
                  ✕
                </button>
              </div>
            </div>
            {loadingAudio ? (
              <div className="flex items-center justify-center h-10 text-gray-400">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Loading audio...
              </div>
            ) : (
            <audio
              controls
              autoPlay
              src={playingUrl}
              className="w-full h-10"
              style={{ filter: 'invert(1)' }}
              onError={(e) => {
                console.error('Audio error:', e.target.error);
                setError(`Audio playback error: ${e.target.error?.message || 'Unknown error'}`);
              }}
            >
              Your browser does not support the audio element.
            </audio>
            )}
          </div>
        </div>
      )}

      {/* Spacer for sticky player */}
      {(playingUrl || loadingAudio) && <div className="h-32"></div>}
    </div>
  );
}

export default CallRecordings;
